import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Txt } from './ui/primitives';
import { DashboardHeader } from './components/DashboardHeader';
import type { AuthUser } from './hooks/useAuth';
import { KpiCards, StatusBreakdown } from './components/KpiCards';
import { PendingPickupPanel } from './components/PendingPickupPanel';
import { StatusDistribution } from './components/StatusDistribution';
import { OrdersSection } from './components/OrdersSection';
import { TopProgressBar } from './components/TopProgressBar';
import { ErrorState, KpiSkeleton, PanelSkeleton } from './components/States';
import { api } from './api/client';
import { useDebounced, useMeta, useOrders, useSummary } from './hooks/useDashboard';
import { loadStoredInterval, useAutoRefresh } from './hooks/useAutoRefresh';
import { StorageKeys, readStorage, writeStorage } from './utils/storage';
import { type DateRange, type ResolveContext, resolvePreset } from './utils/dateRange';
import type { SortField } from './components/OrdersTable';

/**
 * Warehouse Orders Dashboard.
 *
 * Rendered only once a session exists, so none of the data hooks below ever
 * fire an unauthenticated request.
 *
 * The selected date RANGE is the single source of truth for what is on screen;
 * changing it refetches the summary and the table together. Status filter,
 * search, sort and page all reset appropriately so the user never lands on an
 * empty page 7 of a newly filtered result set.
 */
export interface DashboardProps {
  user: AuthUser;
  onLogout: () => void;
  /** Called when the API reports the session has expired mid-session. */
  onSessionExpired: () => void;
}

export default function Dashboard({ user, onLogout, onSessionExpired }: DashboardProps): JSX.Element {
  const meta = useMeta();
  const today = meta.data?.today ?? new Date().toISOString().slice(0, 10);

  const rangeCtx: ResolveContext = useMemo(
    () => ({ today, earliest: meta.data?.earliestOrderDate ?? null }),
    [today, meta.data?.earliestOrderDate],
  );

  // Null until the user picks — defaults to today, resolved once meta lands so
  // "today" is the warehouse's calendar day, not the browser's.
  const [range, setRange] = useState<DateRange | null>(null);
  const effectiveRange: DateRange = range ?? { from: today, to: today };

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 300);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState<SortField>('order_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshIntervalMs, setRefreshIntervalMs] = useState<number>(() => loadStoredInterval());
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = readStorage(StorageKeys.theme);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    writeStorage(StorageKeys.theme, theme);
  }, [theme]);

  // Any change to the filter set invalidates the current page number.
  useEffect(() => setPage(1), [effectiveRange.from, effectiveRange.to, selectedStatuses, search, pageSize]);

  const summary = useSummary(effectiveRange.from, effectiveRange.to, refreshKey);

  const ordersQuery = useMemo(
    () => ({
      from: effectiveRange.from,
      to: effectiveRange.to,
      statuses: selectedStatuses,
      search,
      page,
      pageSize,
      sortBy,
      sortDir,
    }),
    [effectiveRange.from, effectiveRange.to, selectedStatuses, search, page, pageSize, sortBy, sortDir],
  );
  const orders = useOrders(ordersQuery, refreshKey);

  useEffect(() => {
    if (summary.data && !summary.loading) setLastUpdated(new Date());
  }, [summary.data, summary.loading]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  /*
   * Poll for new orders and status changes. Paused automatically while the tab
   * is hidden, and skipped while a previous refresh is still in flight.
   */
  const { active: autoRefreshActive } = useAutoRefresh({
    intervalMs: refreshIntervalMs,
    onRefresh: refresh,
    busy: summary.loading || orders.loading,
  });

  /*
   * A session can expire while the dashboard is open. Any 401 from a data
   * endpoint bounces the user back to the sign-in screen rather than leaving
   * them staring at an error they cannot act on.
   */
  useEffect(() => {
    const codes = [meta.error?.code, summary.error?.code, orders.error?.code];
    if (codes.includes('UNAUTHENTICATED')) onSessionExpired();
  }, [meta.error, summary.error, orders.error, onSessionExpired]);

  const toggleStatus = useCallback((status: string) => {
    setSelectedStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status],
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedStatuses([]);
    setSearchInput('');
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortBy) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortDir('desc');
      }
      setPage(1);
    },
    [sortBy],
  );

  /** Empty-state shortcut: widen to everything the warehouse actually has. */
  const jumpToAllTime = useCallback(() => {
    setRange(resolvePreset('all', rangeCtx));
  }, [rangeCtx]);

  const catalog = meta.data?.statuses ?? [];
  const warehouseLabel = meta.data?.warehouse.label ?? '—';
  const pendingStatuses = summary.data?.pendingPickupStatuses ?? [];
  const pendingStatusLabels = useMemo(
    () => catalog.filter((s) => pendingStatuses.includes(s.value)).map((s) => s.label),
    [catalog, pendingStatuses],
  );

  const exportQuery = {
    from: effectiveRange.from,
    to: effectiveRange.to,
    statuses: selectedStatuses,
    search,
  };
  const exportUrl = api.exportUrl(exportQuery);
  const exportXlsxUrl = api.exportXlsxUrl(exportQuery);

  // A failure to resolve the warehouse or the status catalogue is fatal for the
  // whole page — everything below depends on it.
  if (meta.error) {
    return (
      <Box className="min-h-screen grid place-items-center p-6">
        <Box className="w-full max-w-lg">
          <ErrorState message={meta.error.message} code={meta.error.code} onRetry={meta.retry} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen bg-surface-page">
      <TopProgressBar active={summary.loading || orders.loading} />
      <DashboardHeader
        warehouse={meta.data?.warehouse ?? null}
        range={effectiveRange}
        rangeCtx={rangeCtx}
        onRangeChange={setRange}
        onRefresh={refresh}
        refreshing={summary.loading || orders.loading}
        lastUpdated={lastUpdated}
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
        user={user}
        onLogout={onLogout}
        refreshIntervalMs={refreshIntervalMs}
        onRefreshIntervalChange={setRefreshIntervalMs}
        autoRefreshActive={autoRefreshActive}
      />

      <Box as="main" className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-5">
        {/* KPI row */}
        {summary.error ? (
          <ErrorState message={summary.error.message} code={summary.error.code} onRetry={refresh} compact />
        ) : summary.loading && !summary.data ? (
          <KpiSkeleton />
        ) : summary.data ? (
          <>
            <KpiCards kpis={summary.data.kpis} distribution={summary.data.distribution} />
            <StatusBreakdown distribution={summary.data.distribution} total={summary.data.kpis.total} />
          </>
        ) : null}

        {/* Distribution + pending focus.
            `items-start` lets each card size to its content — stretching them to
            a shared height padded the shorter one with dead space. */}
        <Box className="grid gap-5 grid-cols-1 lg:grid-cols-2 items-start">
          {summary.loading && !summary.data ? (
            <>
              <PanelSkeleton />
              <PanelSkeleton rows={3} />
            </>
          ) : summary.data ? (
            <>
              <StatusDistribution
                distribution={summary.data.distribution}
                total={summary.data.kpis.total}
                activeStatuses={selectedStatuses}
                onSelectStatus={toggleStatus}
              />
              <PendingPickupPanel
                kpis={summary.data.kpis}
                ageing={summary.data.ageing}
                range={effectiveRange}
                rangeCtx={rangeCtx}
                pendingStatusLabels={pendingStatusLabels}
              />
            </>
          ) : null}
        </Box>

        {/* Orders */}
        <OrdersSection
          data={orders.data}
          loading={orders.loading}
          error={orders.error}
          catalog={catalog}
          distribution={summary.data?.distribution ?? []}
          pendingStatuses={pendingStatuses}
          warehouseLabel={warehouseLabel}
          range={effectiveRange}
          rangeCtx={rangeCtx}
          searchInput={searchInput}
          onSearchInput={setSearchInput}
          selectedStatuses={selectedStatuses}
          onToggleStatus={toggleStatus}
          onClearFilters={clearFilters}
          page={page}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={setPageSize}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          exportUrl={exportUrl}
          exportXlsxUrl={exportXlsxUrl}
          onRetry={refresh}
          latestOrderDate={meta.data?.latestOrderDate ?? null}
          onJumpToLatest={jumpToAllTime}
        />

        <Box as="footer" className="pt-2 pb-6">
          <Txt as="p" className="text-[11px] text-ink-muted">
            Scoped to warehouse {warehouseLabel}
            {meta.data?.warehouse.id ? ` (id ${meta.data.warehouse.id})` : ''} · Shipanel database is accessed
            read-only; this dashboard never writes to it.
          </Txt>
        </Box>
      </Box>
    </Box>
  );
}
